import React, { useState, useEffect, useRef } from 'react';
import { Film, Plus, Trash2, RefreshCw, Captions, CheckCircle, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { deleteVideo, saveVideoTranscript } from '../../../services/muxVideoService';
import { generateVideoTranscript } from '../../../services/openaiService';
import MuxVideoPlayer from './MuxVideoPlayer';
import MuxVideoUpload from './MuxVideoUpload';

const POLL_INTERVAL_MS = 8000;
const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

// Caption generation progress states
const CAPTION_STATES = {
  idle: null,
  fetching: 'Fetching audio…',
  transcribing: 'Transcribing…',
  extracting: 'Extracting teaching points…',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'error',
};

const MuxVideoSection = ({ songId, level = null }) => {
  const { isSuperUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [captionStates, setCaptionStates] = useState({}); // { [videoId]: state }
  const [captionErrors, setCaptionErrors] = useState({}); // { [videoId]: errorMsg }
  const pollTimerRef = useRef(null);

  const isAdmin = isSuperUser?.();

  useEffect(() => {
    loadVideos();
    return () => clearTimeout(pollTimerRef?.current);
  }, [songId, level]);

  useEffect(() => {
    clearTimeout(pollTimerRef?.current);
    const hasProcessing = videos?.some(v => v?.status !== 'ready' && v?.status !== 'errored' && v?.status !== 'cancelled');
    if (hasProcessing) {
      pollTimerRef.current = setTimeout(() => {
        loadVideos();
      }, POLL_INTERVAL_MS);
    }
    return () => clearTimeout(pollTimerRef?.current);
  }, [videos]);

  const loadVideos = async () => {
    setError(null);
    try {
      let query = supabase
        ?.from('videos')
        ?.select('id, title, description, status, mux_playback_id, thumbnail_url, duration_seconds, published, created_at, song_id, transcript, captions')
        ?.order('created_at', { ascending: false });

      if (songId) {
        query = query?.eq('song_id', songId);
      }
      if (level) {
        query = query?.eq('level', level);
      }
      if (!isAdmin) {
        query = query?.eq('published', true)?.eq('status', 'ready');
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw new Error(fetchError.message);
      setVideos(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoReady = (newVideo) => {
    setShowUpload(false);
    if (newVideo) {
      setVideos(prev => {
        const exists = prev?.some(v => v?.id === newVideo?.id);
        if (exists) {
          return prev?.map(v => v?.id === newVideo?.id ? { ...v, ...newVideo } : v);
        }
        return [newVideo, ...prev];
      });
    }
    loadVideos();
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    setDeletingId(videoId);
    try {
      await deleteVideo(videoId);
      setVideos(prev => prev?.filter(v => v?.id !== videoId));
    } catch (err) {
      setError(err?.message || 'Failed to delete video');
    } finally {
      setDeletingId(null);
    }
  };

  const setCaptionState = (videoId, state) => {
    setCaptionStates(prev => ({ ...prev, [videoId]: state }));
  };

  const setCaptionError = (videoId, msg) => {
    setCaptionErrors(prev => ({ ...prev, [videoId]: msg }));
  };

  const handleGenerateCaptions = async (videoId, playbackId, video) => {
    setCaptionState(videoId, 'fetching');
    setCaptionError(videoId, null);

    try {
      // Step 1: Fetch audio via server-side edge function (bypasses CORS, tries all rendition formats)
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) throw new Error('Not authenticated');

      const audioRes = await fetch(
        `${SUPABASE_URL}/functions/v1/fetch-mux-audio?playbackId=${encodeURIComponent(playbackId)}`,
        {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
          },
        }
      );

      if (!audioRes?.ok) {
        const errData = await audioRes?.json()?.catch(() => ({}));
        throw new Error(errData?.error || 'Could not fetch video audio from Mux. Ensure the video is ready and MP4 support is enabled.');
      }

      const audioBlob = await audioRes?.blob();
      const contentType = audioRes?.headers?.get('content-type') || 'audio/mp4';
      const ext = contentType?.includes('m4a') || contentType?.includes('mp4a') ? 'm4a' : 'mp4';
      const audioFile = new File([audioBlob], `audio.${ext}`, { type: contentType });

      // Step 2: Transcribe with Whisper
      setCaptionState(videoId, 'transcribing');
      const { transcript, captions: rawCaptions } = await generateVideoTranscript(audioFile, video?.title || '');

      // Step 3: Extract teaching points (already done inside generateVideoTranscript)
      setCaptionState(videoId, 'extracting');
      await new Promise(r => setTimeout(r, 400));

      // Step 4: Save to Supabase
      setCaptionState(videoId, 'saving');
      await saveVideoTranscript(videoId, transcript, rawCaptions);

      // Update local state
      setVideos(prev => prev?.map(v =>
        v?.id === videoId ? { ...v, transcript, captions: rawCaptions } : v
      ));

      setCaptionState(videoId, 'saved');
      setTimeout(() => setCaptionState(videoId, null), 3000);
    } catch (err) {
      console.error('Caption generation failed:', err);
      setCaptionState(videoId, 'error');
      setCaptionError(videoId, err?.message || 'Caption generation failed');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s?.toString()?.padStart(2, '0')}`;
  };

  const getCaptionButtonContent = (video) => {
    const state = captionStates?.[video?.id];
    const hasCaptions = video?.captions?.length > 0;

    if (state === 'fetching') return { label: 'Fetching audio…', loading: true };
    if (state === 'transcribing') return { label: 'Transcribing…', loading: true };
    if (state === 'extracting') return { label: 'Extracting teaching points…', loading: true };
    if (state === 'saving') return { label: 'Saving…', loading: true };
    if (state === 'saved') return { label: 'Saved!', loading: false, success: true };
    if (state === 'error') return { label: 'Retry Captions', loading: false, error: true };
    if (hasCaptions) return { label: 'Regenerate Captions', loading: false };
    return { label: 'Generate Captions', loading: false };
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Videos</h3>
          {videos?.length > 0 && (
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
              {videos?.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadVideos}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Video
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && videos?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
          <Film className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 font-medium">No videos yet</p>
          {isAdmin && (
            <button
              onClick={() => setShowUpload(true)}
              className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Upload the first video
            </button>
          )}
        </div>
      )}

      {/* Video Grid */}
      {!loading && videos?.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          {videos?.map((video) => {
            const btnContent = getCaptionButtonContent(video);
            const captionState = captionStates?.[video?.id];
            const captionErrMsg = captionErrors?.[video?.id];

            return (
              <div key={video?.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                {/* Player */}
                {video?.status === 'ready' && video?.mux_playback_id ? (
                  <MuxVideoPlayer
                    videoId={video?.id}
                    title={video?.title}
                    propThumbnail={video?.thumbnail_url || ''}
                    captions={video?.captions || []}
                    onVideoComplete={() => {}}
                    onWatchEvent={() => {}}
                  />
                ) : (
                  <div className="aspect-video bg-gray-100 flex items-center justify-center">
                    <div className="text-center">
                      <Film className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 capitalize">
                        {video?.status === 'waiting' || video?.status === 'preparing' ? 'Processing video…'
                          : `Status: ${video?.status}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {video?.status !== 'errored' && video?.status !== 'cancelled' ? 'Mux is processing this video. This page will refresh automatically.' : 'Video processing failed. Please re-upload.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{video?.title}</h4>
                      {video?.description && (
                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{video?.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {video?.duration_seconds && (
                          <span className="text-xs text-gray-400">{formatDuration(video?.duration_seconds)}</span>
                        )}
                        {isAdmin && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            video?.published
                              ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {video?.published ? 'Published' : 'Draft'}
                          </span>
                        )}
                        {isAdmin && video?.status !== 'ready' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium capitalize">
                            {video?.status}
                          </span>
                        )}
                        {video?.captions?.length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-1">
                            <Captions className="w-3 h-3" />
                            {video?.captions?.length} captions
                          </span>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(video?.id)}
                        disabled={deletingId === video?.id}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                        title="Delete video"
                      >
                        {deletingId === video?.id ? (
                          <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Admin: Generate Captions button */}
                  {isAdmin && video?.status === 'ready' && video?.mux_playback_id && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleGenerateCaptions(video?.id, video?.mux_playback_id, video)}
                        disabled={btnContent?.loading}
                        className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                          btnContent?.success
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : btnContent?.error
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : btnContent?.loading
                            ? 'bg-purple-100 text-purple-600 cursor-not-allowed' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        {btnContent?.loading ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : btnContent?.success ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Captions className="w-4 h-4" />
                        )}
                        {btnContent?.label}
                      </button>
                      {captionState === 'error' && captionErrMsg && (
                        <p className="text-xs text-red-600 mt-1">{captionErrMsg}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <MuxVideoUpload
          onVideoReady={handleVideoReady}
          onClose={() => setShowUpload(false)}
          songId={songId}
          level={level}
        />
      )}
    </div>
  );
};

export default MuxVideoSection;
